
import { useCallback, Dispatch, SetStateAction } from 'react';
import { AppNode, ProcessDefinition, Edge } from '../types';
import { saveProjectToFile, loadProjectFromFile } from '../utils/pssHelper';
// Fix: Use named import for useReactFlow from reactflow.
import { useReactFlow } from 'reactflow';
import { useToast } from '../contexts/ToastContext';

export const useFileOperations = (
    nodes: AppNode[],
    edges: Edge[],
    processes: ProcessDefinition[],
    setProcesses: Dispatch<SetStateAction<ProcessDefinition[]>>,
    activeProcessId: string,
    setActiveProcessId: Dispatch<SetStateAction<string>>,
    setNodes: Dispatch<SetStateAction<AppNode[]>>,
    setEdges: Dispatch<SetStateAction<Edge[]>>,
    setIsLoading: Dispatch<SetStateAction<boolean>>
) => {
    const { fitView } = useReactFlow();
    const { addToast } = useToast();

    const handleSaveProject = useCallback(async () => {
        setIsLoading(true);
        try {
            const allProcesses = processes.map(p => 
                p.id === activeProcessId ? { ...p, nodes, edges } : p
            );
            saveProjectToFile(allProcesses, "Peak_Workflow_Project");
            addToast({ type: 'success', title: 'Project Saved', message: 'Workflow project exported successfully.' });
        } catch (error: any) {
            addToast({ type: 'error', title: 'Save Failed', message: error.message || 'Could not export project.' });
        } finally {
            setIsLoading(false);
        }
    }, [nodes, edges, processes, activeProcessId, addToast, setIsLoading]);

    const handleLoadProject = useCallback(async (file: File) => {
        // Pre-validation Checks
        if (!file.name.toLowerCase().endsWith('.json')) {
            addToast({ type: 'error', title: 'Invalid File Type', message: 'Only .json files are supported.' });
            return;
        }

        const MAX_SIZE_MB = 10;
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            addToast({ type: 'error', title: 'File Too Large', message: `File exceeds ${MAX_SIZE_MB}MB limit.` });
            return;
        }

        setIsLoading(true);
        try {
            const loadedProcesses = await loadProjectFromFile(file);
            
            if (!loadedProcesses || loadedProcesses.length === 0) {
                 addToast({ type: 'error', title: 'Import Failed', message: "No valid processes found in file." });
                 return;
            }

            setProcesses(loadedProcesses);
            
            const firstProc = loadedProcesses[0];
            setNodes(firstProc.nodes);
            setEdges(firstProc.edges);
            setActiveProcessId(firstProc.id);
            
            setTimeout(() => fitView(), 100);
            addToast({ type: 'success', title: 'Project Loaded', message: `Successfully loaded ${loadedProcesses.length} processes.` });
        } catch (error: any) {
            console.error("Failed to load project:", error);
            addToast({ type: 'error', title: 'Import Error', message: error.message || "Failed to load project file." });
        } finally {
            setIsLoading(false);
        }
    }, [setNodes, setEdges, setProcesses, setActiveProcessId, fitView, addToast, setIsLoading]);

    return {
        handleSaveProject,
        handleLoadProject
    };
};
